/**
 * AppSidebar - canonical product navigation.
 *
 * Exposes only real, working routes. No dead links, no fake modules.
 * Structure: BUILD → SHIP → ACCOUNT
 */

import { useLocation } from "react-router-dom";
import { NuvraLogo } from "@/components/NuvraLogo";
import { NavLink } from "@/components/NavLink";
import { getPlatformStatus } from "@/services/platformStatusService";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarFooter,
  useSidebar,
} from "@/components/ui/sidebar";
import { navigationGroups } from "@/services/navigationContext";
import { UserAvatarMenu } from "@/components/UserAvatarMenu";
import { useI18n } from "@/i18n/I18nContext";

export function AppSidebar() {
  const { state, isMobile, setOpenMobile } = useSidebar();
  const { t } = useI18n();
  const collapsed  = state === "collapsed";
  const location   = useLocation();
  // On mobile the sidebar is an overlay sheet; tapping a destination must close
  // it so the page is revealed without a second gesture.
  const closeOnMobile = () => { if (isMobile) setOpenMobile(false); };
  const status     = getPlatformStatus();
  const hasOutage  = status.services.some((s) => s.status === "outage");
  const hasDegraded = !hasOutage && status.services.some((s) => s.status === "degraded");
  const footerDot  = hasOutage ? "bg-red-500" : hasDegraded ? "bg-amber-500" : "bg-emerald-500";
  const footerText = hasOutage
    ? t("footer.status.outage")
    : hasDegraded ? t("footer.status.degraded") : t("footer.status.nominal");

  return (
    <Sidebar collapsible="icon" className="border-r border-border/30 bg-background/95 backdrop-blur-sm">

      {/* Brand */}
      <div className="flex h-14 items-center gap-2.5 px-4 border-b border-border/30">
        <NuvraLogo size={28} className="shrink-0" />
        {!collapsed && (
          <div className="flex flex-col leading-none">
            <span className="text-[15px] font-bold tracking-tight gradient-text">Nuvra</span>
          </div>
        )}
      </div>

      {/* Navigation - clean, quiet, route-only. The inline recent-projects list was
          removed: it surfaced raw build names (and in-progress builds) at the top,
          which read as clutter, not a premium platform. Projects are reached through
          the Projects route below. */}
      <SidebarContent className="mt-2 px-2 flex-1">
        {navigationGroups.map((group) => (
          <SidebarGroup key={group.label} className="py-1">
            {!collapsed && (
              <SidebarGroupLabel className="text-[9px] uppercase tracking-widest text-muted-foreground/40 font-semibold px-3 mb-1">
                {t(group.labelKey)}
              </SidebarGroupLabel>
            )}
            <SidebarGroupContent>
              <SidebarMenu>
                {group.items.map((item) => {
                  const isActive = location.pathname === item.url ||
                    (item.url !== "/dashboard" && (
                      location.pathname.startsWith(item.url + "/") ||
                      location.pathname === item.url
                    ));
                  return (
                    <SidebarMenuItem key={item.url}>
                      <SidebarMenuButton asChild>
                        <NavLink
                          to={item.url}
                          end={item.url === "/dashboard"}
                          onClick={closeOnMobile}
                          className={[
                            "flex items-center gap-3 rounded-lg px-3 py-2 text-[13px] transition-all duration-150",
                            "text-muted-foreground hover:bg-muted/60 hover:text-foreground",
                            "relative",
                          ].join(" ")}
                          activeClassName="bg-primary/10 text-primary font-medium"
                        >
                          {/* Active left-border indicator */}
                          {isActive && (
                            <span className="absolute left-0 top-1/2 -translate-y-1/2 h-4 w-0.5 rounded-r bg-primary" />
                          )}
                          <item.icon className="h-4 w-4 shrink-0" />
                          {!collapsed && <span>{t(item.titleKey)}</span>}
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>

      {/* Footer - status, then the account as the last section (settings, billing,
          sign out live here, moved out of the top bar to keep the header clean). */}
      {!collapsed && (
        <SidebarFooter className="px-2 py-2 border-t border-border/20 gap-1.5">
          {/* The live status indicator is now the WAY IN to status, not just a
              readout. The nav used to carry a "System Status" item pointing at
              the operator console, which no customer could open; this points at
              the panel that actually answers the question, and it is the only
              status affordance a customer needs. */}
          <NavLink
            to="/settings?section=status"
            onClick={closeOnMobile}
            className="flex items-center gap-2 px-2 py-1 rounded-md text-muted-foreground/50 transition-colors hover:bg-muted/20 hover:text-muted-foreground"
          >
            <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${footerDot} ${!hasOutage && !hasDegraded ? "animate-pulse" : ""}`} />
            <span className="text-[10px] font-mono">{footerText}</span>
          </NavLink>
          <UserAvatarMenu variant="sidebar" />
        </SidebarFooter>
      )}
    </Sidebar>
  );
}
